<template>
  <div class="lp-home">
    <section class="lp-hero lp-hero--bleed" aria-labelledby="home-title">
      <div class="lp-hero-backdrop" aria-hidden="true">
        <UiSkeleton v-if="hero.status === 'loading'" class="lp-hero-backdrop-media lp-hero-skeleton" block :lines="1" />
        <PublicMedia v-else class="lp-hero-backdrop-media" :src="heroImage" :alt="''" :fallback="heroFallback" eager :width="1920" :height="900" />
      </div>
      <PublicPageContainer class="lp-hero-foreground">
        <div class="lp-hero-copy">
          <p class="lu-eyebrow">{{ $t('publicSite.brand') }}</p>
          <h1 id="home-title" class="lu-hero-title">{{ heroTitle }}</h1>
          <PublicContentState :state="hero" @retry="load('hero')">
            <p class="lp-lead">{{ heroLead }}</p>
          </PublicContentState>
          <div class="lu-inline lp-hero-actions">
            <a href="#tracking" class="lu-button lu-button--primary">{{ $t('publicSite.trackShipment') }}<UiIcon name="arrow" class="lu-directional" /></a>
            <a href="#contact" class="lu-button lu-button--outline lp-hero-ghost">{{ $t('ui.contactTeam') }}</a>
          </div>
        </div>
        <TrackingSearch class="lp-hero-track" />
      </PublicPageContainer>
    </section>

    <section id="about" class="lp-section" aria-labelledby="about-title">
      <PublicPageContainer>
        <h2 v-if="about.status !== 'ready'" id="about-title" class="lu-sr-only">{{ $t('ui.about') }}</h2>
        <PublicContentState :state="about" block @retry="load('about')">
          <div class="lp-split">
            <PublicMedia :src="about.data?.image || ''" :alt="about.data?.title || ''" :fallback="aboutFallback" />
            <div class="lu-stack"><PublicSectionHeader id="about-title" :eyebrow="$t('ui.about')" :title="about.data?.title || $t('ui.about')" /><p class="lp-description">{{ about.data?.description }}</p><a href="#contact" class="lu-link">{{ $t('ui.contactTeam') }} <UiIcon name="arrow" class="lu-directional" /></a></div>
          </div>
        </PublicContentState>
      </PublicPageContainer>
    </section>

    <section id="services" class="lp-section lp-section--canvas" aria-labelledby="services-title">
      <PublicPageContainer><PublicSectionHeader id="services-title" :title="$t('ui.services')" />
        <PublicContentState :state="services" block @retry="load('services')">
          <div class="lp-card-grid"><UiCard v-for="(service,index) in services.data" :key="index" as="article" class="lp-service-card" flat>
            <PublicMedia :src="service.image" :alt="service.title" :width="640" :height="400" />
            <div class="lp-card-copy"><h3 v-if="service.title">{{ service.title }}</h3><p v-if="service.description">{{ service.description }}</p></div>
          </UiCard></div>
        </PublicContentState>
      </PublicPageContainer>
    </section>

    <section id="choose" class="lp-section" aria-labelledby="choose-title">
      <PublicPageContainer><div class="lp-choose-grid">
        <div class="lu-stack"><PublicSectionHeader id="choose-title" :title="$t('ui.whyChoose')" />
          <PublicContentState :state="choose" @retry="load('choose')"><p v-if="choose.data?.description" class="lp-description">{{ choose.data.description }}</p><PublicMedia class="lp-choose-image" :src="choose.data?.image || ''" :alt="choose.data?.title || ''" :fallback="chooseFallback" /></PublicContentState>
        </div>
        <PublicContentState :state="advantages" @retry="load('advantages')"><ul class="lp-advantages"><li v-for="(item,index) in advantages.data" :key="index"><PublicMedia :src="item.image" :alt="item.title" contain :width="80" :height="80" /><div><h3 v-if="item.title">{{ item.title }}</h3><p v-if="item.description">{{ item.description }}</p></div></li></ul></PublicContentState>
      </div></PublicPageContainer>
    </section>

    <section id="reviews" class="lp-section lp-section--navy" aria-labelledby="reviews-title">
      <PublicPageContainer><PublicSectionHeader id="reviews-title" :title="$t('our clients')" />
        <PublicContentState :state="reviews" @retry="load('reviews')"><div class="lp-card-grid">
          <UiCard v-for="(review,index) in reviews.data" :key="index" as="figure" class="lp-review" flat>
            <span class="lp-quote" aria-hidden="true">“</span><blockquote v-if="review.description"><p>{{ review.description }}</p></blockquote>
            <figcaption><PublicMedia :src="review.image" :alt="review.title" :width="80" :height="80" /><div><h3 v-if="review.title">{{ review.title }}</h3><p v-if="review.created_at" class="lu-muted"><bdi>{{ formatDate(review.created_at) }}</bdi></p></div></figcaption>
          </UiCard>
        </div></PublicContentState>
      </PublicPageContainer>
    </section>

    <section id="sponsors" class="lp-section lp-partners" aria-labelledby="sponsors-title">
      <PublicPageContainer><PublicSectionHeader id="sponsors-title" :title="$t('our sponsors')" />
        <PublicContentState :state="sponsors" @retry="load('sponsors')"><ul class="lp-partner-grid"><li v-for="(sponsor,index) in sponsors.data" :key="index"><PublicMedia :src="sponsor.image" :alt="$t('publicSite.partnerLogo', { number: index + 1 })" contain :width="240" :height="140" /></li></ul></PublicContentState>
      </PublicPageContainer>
    </section>

    <section id="contact" class="lp-section lp-section--canvas" aria-labelledby="contact-title">
      <PublicPageContainer><div class="lp-contact-grid"><div class="lu-stack lp-contact-copy">
        <PublicSectionHeader id="contact-title" :title="$t('ui.contact')" :description="$t('publicSite.contactHelp')" />
        <PublicContentState :state="settingsState" @retry="$emit('retry-settings')"><div class="lp-contact-details">
          <a v-if="settingsState.data?.email" :href="'mailto:'+settingsState.data.email"><span>{{ $t('publicSite.email') }}</span><bdi>{{ settingsState.data.email }}</bdi></a>
          <a v-if="settingsState.data?.phone" :href="'tel:'+settingsState.data.phone"><span>{{ $t('publicSite.phone') }}</span><bdi>{{ settingsState.data.phone }}</bdi></a>
          <a v-for="social in socialLinks" :key="social.key" :href="social.url" target="_blank" rel="noopener noreferrer">{{ $t('ui.'+social.key) }}<span class="lu-sr-only"> — {{ $t('ui.newWindow') }}</span><UiIcon name="arrow" class="lu-directional" /></a>
        </div></PublicContentState>
      </div><UiCard class="lp-contact-card" as="div"><ContactForm /></UiCard></div></PublicPageContainer>
    </section>
  </div>
</template>

<script>
import { headerData } from '@/api/Home/header';
import { aboutData } from '@/api/Home/about';
import { servicesData } from '@/api/Home/services';
import { choosePageData, chooseData } from '@/api/Home/choose';
import { reviewsData } from '@/api/Home/reviews';
import { sponsersData } from '@/api/Home/sponsers';
import { UiCard, UiIcon, UiSkeleton } from '@/components/ui';
import PublicPageContainer from '@/components/layouts/PublicPageContainer.vue';
import PublicSectionHeader from '@/components/layouts/PublicSectionHeader.vue';
import PublicContentState from '@/components/public/PublicContentState.vue';
import PublicMedia from '@/components/public/PublicMedia.vue';
import TrackingSearch from '@/components/public/TrackingSearch.vue';
import ContactForm from '@/components/public/ContactForm.vue';
import { resource, loadResource, safeWebUrl } from '@/components/public/content';
import heroFallback from '@/assets/media/header.jpg';
import aboutFallback from '@/assets/media/about.jpg';
import chooseFallback from '@/assets/media/choose.jpg';
import { isGenericCmsCopy, isLogoCmsImage } from '@/components/public/content';
const requests = { hero: [headerData, 'page'], about: [aboutData, 'page'], services: [servicesData, 'list'], choose: [choosePageData, 'page'], advantages: [chooseData, 'list'], reviews: [reviewsData, 'list'], sponsors: [sponsersData, 'sponsors'] };
export default {
  name: 'HomePage',
  components: { UiCard, UiIcon, UiSkeleton, PublicPageContainer, PublicSectionHeader, PublicContentState, PublicMedia, TrackingSearch, ContactForm },
  props: { settingsState: { type: Object, default: () => ({ status: 'empty', data: null }) } }, emits: ['retry-settings'],
  data: () => ({ hero: resource(), about: resource(), services: resource(), choose: resource(), advantages: resource(), reviews: resource(), sponsors: resource(), heroFallback, aboutFallback, chooseFallback, previousTitle: '', previousDescription: null, hadDescription: false, active: true }),
  computed: {
    socialLinks() { return ['whatsapp','facebook','twitter','linkedin','instagram'].map(key => ({ key, url: safeWebUrl(this.settingsState.data?.[key]) })).filter(item => /^https?:\/\//i.test(item.url)); },
    // CMS /page/header currently returns placeholder AR copy + a logo PNG for both locales.
    heroTitle() {
      const title = this.hero.data?.title;
      return isGenericCmsCopy(title) ? this.$t('publicSite.homeTitle') : String(title);
    },
    heroLead() {
      const description = this.hero.data?.description;
      return isGenericCmsCopy(description) ? this.$t('publicSite.homeLead') : String(description).replace(/\s+/g, ' ').trim();
    },
    heroImage() {
      const image = this.hero.data?.image;
      return isLogoCmsImage(image) ? '' : (image || '');
    },
  },
  created() { Object.keys(requests).forEach(key => this.load(key)); },
  mounted() {
    this.previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    this.hadDescription = !!meta; this.previousDescription = meta?.content ?? null;
    this.updateMetadata();
  },
  beforeUnmount() {
    this.active = false; document.title = this.previousTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (this.hadDescription && meta) meta.content = this.previousDescription;
    else if (meta) meta.remove();
  },
  methods: {
    async load(key) { const [request, kind] = requests[key]; await loadResource(this[key], request, kind); if (key === 'hero' && this.active && this.$el) this.updateMetadata(); },
    updateMetadata() {
      document.title = this.heroTitle + ' | Leader';
      const description = this.heroLead;
      let meta = document.querySelector('meta[name="description"]');
      if (description) { if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); } meta.content = description; }
      else if (meta) meta.remove();
    },
    formatDate(value) { const match = /^(\d{4})-(\d{2})-(\d{2})(?:T|\s|$)/.exec(value); return match ? match[3] + '/' + match[2] + '/' + match[1] : value; }
  }
};
</script>
